// src/components/compliance/SubjectComplianceSection.tsx
//
// Reusable Compliance section for a single subject (Contact / Provider
// User / Department Circle). Renders:
//   - the backend-evaluated eligibility summary (overall + line items)
//   - the subject's documents
//
// Backend is authoritative: this component NEVER re-derives severity
// or satisfies-status client-side.

import {
  useEvaluateComplianceSubjectQuery,
  useGetComplianceDocumentsQuery,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import { DocumentStatusChip, EligibilityChip, EnforcementModeChip } from './Chips';
import {
  formatShortDate,
  messageFromComplianceError,
  SUBJECT_TYPE_LABEL,
} from './complianceDisplay';
import type { ComplianceSubjectType } from '../../types/api';
import { useComplianceRealtime } from '../../hooks/useComplianceRealtime';

interface SubjectComplianceSectionProps {
  workspaceId: string;
  subjectType: ComplianceSubjectType;
  subjectId: string;
  /** Compact variant for slide-overs; hides the doc list. */
  compact?: boolean;
}

export default function SubjectComplianceSection({
  workspaceId,
  subjectType,
  subjectId,
  compact = false,
}: SubjectComplianceSectionProps) {
  useComplianceRealtime({ workspaceId, subjectType, subjectId });

  const {
    data: eligibility,
    isLoading: eLoading,
    error: eError,
    refetch,
  } = useEvaluateComplianceSubjectQuery({ subjectType, subjectId }, { skip: !subjectId });

  const {
    data: docs,
    isLoading: dLoading,
  } = useGetComplianceDocumentsQuery(
    { subjectType, subjectId, limit: 200 },
    { skip: compact || !subjectId },
  );

  if (!subjectId) return null;
  if (eLoading) return <LoadingSpinner />;
  if (eError) {
    return <ErrorState title="Could not evaluate compliance" message={messageFromComplianceError(eError)} onRetry={refetch} />;
  }
  if (!eligibility) return null;

  return (
    <div className="space-y-4" data-testid="subject-compliance-section">
      <div className="rounded-lg border border-gray-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-gray-500">Overall</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <EligibilityChip severity={eligibility.status} />
            <span className="text-xs text-gray-500">
              Evaluated {formatShortDate(eligibility.evaluatedAt)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Subject</div>
          <div className="text-sm text-gray-800">{SUBJECT_TYPE_LABEL[subjectType]}</div>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white">
        <header className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Requirements</h3>
          <span className="text-xs text-gray-500">{eligibility.requirements.length} in scope</span>
        </header>
        {eligibility.requirements.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No requirements apply here.</div>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="subject-compliance-req-list">
            {eligibility.requirements.map((line) => (
              <li key={line.requirementId} className="p-3" data-testid={`subject-compliance-req-${line.requirementId}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{line.requirementName}</span>
                      <EligibilityChip severity={line.severity} />
                      <EnforcementModeChip mode={line.enforcement} />
                    </div>
                    <div className="mt-0.5 text-xs text-gray-600">{line.reason}</div>
                    {line.expiresAt && (
                      <div className="mt-0.5 text-xs text-gray-500">
                        Expires {formatShortDate(line.expiresAt)}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!compact && (
        <section className="rounded-lg border border-gray-200 bg-white">
          <header className="px-4 py-2 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
          </header>
          {dLoading ? (
            <div className="p-4"><LoadingSpinner size="sm" /></div>
          ) : (docs?.rows ?? []).length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No documents for this subject yet.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {(docs?.rows ?? []).map((doc) => (
                <li key={doc.id} className="p-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                    <div className="text-xs text-gray-500">
                      {doc.requirement?.name ?? '—'} · Expires {formatShortDate(doc.expiresAt)}
                    </div>
                  </div>
                  <DocumentStatusChip status={doc.status} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
